<script setup lang="ts">
import {
  type IntakeAgentInDb,
  type IntakeAgentUpdate,
  type IntakeAgentUpdatePlaybooks,
  type IntakeAgentUpdatePromptCollections,
  type EquitySectionInDb,
  IntegrationState,
  type JiraIntegrationStatus,
  type JiraWebhookConfig,
  type SearchQuery,
  type SlackIntegrationStatus,
  type AllowListCreate,
  type AllowListInDb,
  searchIntakeAgent,
  updateIntakeAgent as updateIntakeAgentApi,
  deleteIntakeAgent,
  updateIntakeAgentPromptCollections,
  updateIntakeAgentPlaybooks,
  searchAllowList,
  createAllowListEntry,
  deleteAllowListEntry,
  searchEquitySections,
  deleteSection,
} from '@/api';
import { useIntegrationsStore } from '@/stores/integrationsStore';
import { usePromptCollectionsStore } from '@/stores/promptCollectionsStore';
import { NFlex, type SelectOption } from 'naive-ui';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import { gaiaContentWidth } from '@/components/design/themes/tokens';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import { computed, h, onMounted, ref, VNode, watch } from 'vue';
import { extractErrorDetail } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaSwitch from '@/components/design/base/GaiaSwitch.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';
import GaiaRemoteSelect from '@/components/design/base/selects/GaiaRemoteSelect.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaAddButton from '@/components/design/base/GaiaAddButton.vue';
import GaiaCreateButton from '@/components/design/base/GaiaCreateButton.vue';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import GaiaCodeBox from '@/components/design/base/GaiaCodeBox.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaSectionHeader from '@/components/design/base/GaiaSectionHeader.vue';
import GaiaStatusChip from '@/components/design/base/GaiaStatusChip.vue';
import GaiaPasswordInput from '@/components/design/base/inputs/GaiaPasswordInput.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaPopconfirm from '@/components/design/composite/GaiaPopconfirm.vue';
import GaiaToggleRow from '@/components/design/composite/GaiaToggleRow.vue';
import GaiaTabBar from '@/components/design/composite/GaiaTabBar.vue';
import { fetchJiraIntegrationStatus } from '@/services/integration/jiraIntegrationService';
import IntakeAgentCreateModal from '@/components/intake-agent/IntakeAgentCreateModal.vue';
import {
  AGENT_MODAL_TABS,
  ALLOWLIST_ENTRY_TYPE_OPTIONS,
  CLM_SECTION_OPTIONS,
  SIGNING_STATUS_OPTIONS,
  WEBHOOK_METHOD_OPTIONS,
  capabilitiesError,
  createEquitySections,
  fetchPlaybooks,
  getAllAvailableSections,
  isValidDomain,
  isValidEmail,
  isValidUrl,
  onPlaybookSearchError,
  promptCollectionsError,
  useCapabilitiesOptions,
  useClmSections,
  useGaiaSectionOptions,
  usePromptCollectionOptions,
} from '@/components/intake-agent/intakeAgentForm';
import {
  disableAgentJiraWebhook,
  enableAgentJiraWebhook,
  fetchJiraWebhookConfig,
  rotateAgentJiraWebhook,
} from '@/services/integration/jiraWebhookService';
import {
  fetchSlackIntegrationStatus,
  hasAllSlackScopes,
  setAgentSlackEnabled,
} from '@/services/integration/slackIntegrationService';
// Use Naive UI's components for notifications and dialogs
const message = useGaiaToast();

/**
 * The copy glyph, drawn here rather than pulled from an icon pack: these column
 * cells are `h()` render functions, so the icon has to be a render call too.
 */
function copyGlyph(size = 14): VNode {
  return h(
    'svg',
    {
      width: size,
      height: size,
      viewBox: '0 0 16 16',
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': 1.5,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
    },
    [
      h('rect', { x: 5.5, y: 5.5, width: 8, height: 8, rx: 1.6 }),
      h('path', {
        d: 'M10.5 3.2A1.7 1.7 0 0 0 8.8 2H3.7A1.7 1.7 0 0 0 2 3.7v5.1c0 .8.5 1.4 1.2 1.6',
      }),
    ]
  );
}

function trashGlyph(size = 13): VNode {
  return h(
    'svg',
    {
      width: size,
      height: size,
      viewBox: '0 0 16 16',
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': 1.5,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
    },
    [h('path', { d: 'M3 4.5h10M6.5 4.5V3h3v1.5M4.5 4.5l.6 8.1h5.8l.6-8.1' })]
  );
}

// Stores
const promptCollectionsStore = usePromptCollectionsStore();
const integrationsStore = useIntegrationsStore();

const capabilitiesOptions = useCapabilitiesOptions();
const promptCollectionOptions = usePromptCollectionOptions();
const gaiaSectionOptions = useGaiaSectionOptions();

const intakeAgents = ref<IntakeAgentInDb[]>([]);
const isLoading = ref(true);
const selectedAgent = ref<IntakeAgentInDb | null>(null);
const allowlistEntries = ref<AllowListInDb[]>([]);

// Form for creating new allowlist entry
const newAllowlistForm = ref<AllowListCreate>({
  email: '',
  domain: '',
  is_user: false,
  is_domain: false,
});

// Allowlist entry type selection
const allowlistType = ref<'email' | 'domain' | 'user'>('email');

// Webhook form data
const webhookUrl = ref('');
const webhookMethod = ref<'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'>('POST');
const webhookHeaders = ref<Record<string, string>>({});
const webhookTimeout = ref<string>('30');
const newHeaderKey = ref('');
const newHeaderValue = ref('');
const showWebhookForm = ref(true);

// Equity sections management
const equitySections = ref<EquitySectionInDb[]>([]);

// Watch allowlist type and update form accordingly
watch(allowlistType, newType => {
  newAllowlistForm.value.is_domain = newType === 'domain';
  newAllowlistForm.value.is_user = newType === 'user';

  // Reset the email/domain field when switching types
  newAllowlistForm.value.email = '';
  newAllowlistForm.value.domain = '';
});

// Modal states
const showNewAgentModal = ref(false);
const showNewAllowlistModal = ref(false);
const showAllowlistManagerModal = ref(false);
const showEditAgentModal = ref(false);

// Tab state for the edit modal
const editModalActiveTab = ref('capabilities');

// Filename denylist management
const editFilenameDenylist = ref<{ match_string: string; exact_match: boolean }[]>([]);

// Allowlist form management for edit modal
const editAllowlistEntries = ref<
  { type: 'email' | 'domain' | 'user'; value: string; uid?: string }[]
>([]);

// Form validation trigger states
const editFormSubmitted = ref(false);
const allowlistFormSubmitted = ref(false);

// The webhook fields only exist while the form is open, so they only validate there.
const webhookUrlError = computed<string | null>(() => {
  if (!showWebhookForm.value) return null;
  if (!webhookUrl.value.trim()) return 'Webhook URL is required when webhook is enabled';
  if (!isValidUrl(webhookUrl.value)) return 'Please enter a valid webhook URL';
  return null;
});

const webhookTimeoutError = computed<string | null>(() => {
  if (!showWebhookForm.value) return null;
  if (webhookTimeout.value && isNaN(Number(webhookTimeout.value))) {
    return 'Webhook timeout must be a valid number';
  }
  return null;
});

const editNameError = computed<string | null>(() =>
  editAgentForm.value.name?.trim() ? null : 'Agent name is required'
);
const editCapabilitiesError = computed(() => capabilitiesError(editAgentForm.value.capabilities));
const editPromptCollectionsError = computed(() =>
  promptCollectionsError(editAgentForm.value.capabilities, editAgentForm.value.prompt_collections)
);

const allowlistValueError = computed<string | null>(() => {
  const value = newAllowlistForm.value.email;
  if (!value) {
    return allowlistType.value === 'domain'
      ? 'Please enter a domain'
      : 'Please enter an email address';
  }
  if (allowlistType.value === 'domain') {
    return isValidDomain(value) ? null : 'Please enter a valid domain (e.g., example.com)';
  }
  return isValidEmail(value) ? null : 'Please enter a valid email address';
});

// Watch modal state to reset the edit form when closed
watch(showEditAgentModal, isOpen => {
  if (!isOpen) {
    // Reset form when edit modal is closed
    editAgentForm.value = {
      name: '',
      description: '',
      allow_all_senders: false,
      allow_api_requests: false,
      respond_with_summary: false,
      prompt_collections: [],
      playbooks: [],
      clm_sections: 'none',
      capabilities: [],
      callback_webhook: null,
      filename_deny_list: null,
    };
    // Reset webhook form data
    webhookUrl.value = '';
    webhookMethod.value = 'POST';
    webhookHeaders.value = {};
    webhookTimeout.value = '30';
    editSelectedSpecificSections.value = [];
    editSelectedDefaultSection.value = undefined;
    originalAgentData.value = null;
    editFilenameDenylist.value = [];
    editModalActiveTab.value = 'capabilities';
    editAllowlistEntries.value = [];
    editFormSubmitted.value = false;
    jiraWebhookConfig.value = null;
    jiraOrgStatus.value = null;
    jiraOrgStatusLoaded.value = false;
    jiraWebhookLoaded.value = false;
    slackOrgStatus.value = null;
    slackOrgStatusLoaded.value = false;
    slackEnabledOverride.value = null;
  }
});

// Agent being edited
const editAgentForm = ref<
  IntakeAgentUpdate & {
    prompt_collections: string[];
    playbooks: string[];
    clm_sections: string;
    capabilities: string[];
  }
>({
  name: '',
  description: '',
  allow_all_senders: false,
  allow_api_requests: false,
  respond_with_summary: false,
  prompt_collections: [],
  playbooks: [],
  clm_sections: 'none',
  capabilities: [],
  callback_webhook: null,
  inbound_document_signing_status: null,
});
const editAgentUid = ref<string>('');

// Store the original agent data for editing
const originalAgentData = ref<IntakeAgentInDb | null>(null);

// Edit form specific sections
const editSelectedSpecificSections = ref<string[]>([]);
const editSelectedDefaultSection = ref<string | undefined>(undefined);

// Loading state for edit agent save
const isEditAgentSaving = ref(false);

// Loading state for CLM sections
const { isLoading: isLoadingClmSections, load: loadClmSections } = useClmSections();

/** Labels for the edited agent's already-linked playbooks (see openEditModal). */
const editPlaybookSeeds = ref<SelectOption[]>([]);

// ==================== Jira webhook ====================

// `jira_webhook_enabled` is settable on IntakeAgentCreate and readable on IntakeAgentInDb, but is
// not part of IntakeAgentUpdate — for an existing agent the four webhook endpoints are the only
// way to change it, since they also mint and drop the URL's token. So these refs are deliberately
// outside editAgentForm: they take effect immediately rather than being flushed by "Save Changes".
const jiraWebhookConfig = ref<JiraWebhookConfig | null>(null);
const jiraOrgStatus = ref<JiraIntegrationStatus | null>(null);
const jiraOrgStatusLoaded = ref(false);
const jiraWebhookLoading = ref(false);
const jiraWebhookActionLoading = ref(false);
const jiraWebhookLoaded = ref(false);

const isJiraOrgConnected = computed(
  () => jiraOrgStatus.value?.state === IntegrationState.CONNECTED
);

// The agent record carries the flag, so the state is known before the lazy config fetch lands.
const isJiraWebhookEnabled = computed(
  () => jiraWebhookConfig.value?.enabled ?? originalAgentData.value?.jira_webhook_enabled ?? false
);

// The org connection can flip to disconnected server-side (revoked consent, dead refresh token),
// so it is read fresh when a Jira tab is opened rather than cached with the agent list.
async function loadJiraOrgStatusIfNeeded(): Promise<void> {
  if (jiraOrgStatusLoaded.value) return;

  jiraOrgStatus.value = await fetchJiraIntegrationStatus();
  jiraOrgStatusLoaded.value = true;
}

// Loaded lazily when the edit modal's Jira tab is first opened.
async function loadJiraWebhookIfNeeded(): Promise<void> {
  if (jiraWebhookLoaded.value || !editAgentUid.value) return;

  jiraWebhookLoading.value = true;
  try {
    const [, config] = await Promise.all([
      loadJiraOrgStatusIfNeeded(),
      fetchJiraWebhookConfig(editAgentUid.value),
    ]);
    jiraWebhookConfig.value = config;
    jiraWebhookLoaded.value = true;
  } catch (error) {
    logger.error('Failed to load Jira webhook configuration:', error);
    message.error(extractErrorDetail(error, 'Failed to load the Jira webhook configuration.'));
  } finally {
    jiraWebhookLoading.value = false;
  }
}

function handleEditModalTabChange(tab: string): void {
  editModalActiveTab.value = tab;
  if (tab === 'jira') {
    void loadJiraWebhookIfNeeded();
  }
  if (tab === 'slack') {
    void loadSlackOrgStatusIfNeeded();
  }
}

async function handleEnableJiraWebhook(): Promise<void> {
  if (!editAgentUid.value) return;

  jiraWebhookActionLoading.value = true;
  try {
    jiraWebhookConfig.value = await enableAgentJiraWebhook(editAgentUid.value);
    message.success('Jira webhook enabled.');
    // The agent row carries jira_webhook_enabled, so the table would otherwise go stale.
    await loadIntakeAgents();
  } catch (error) {
    logger.error('Failed to enable the Jira webhook:', error);
    message.error(extractErrorDetail(error, 'Failed to enable the Jira webhook.'));
  } finally {
    jiraWebhookActionLoading.value = false;
  }
}

async function handleRotateJiraWebhook(): Promise<void> {
  if (!editAgentUid.value) return;

  jiraWebhookActionLoading.value = true;
  try {
    jiraWebhookConfig.value = await rotateAgentJiraWebhook(editAgentUid.value);
    message.success('Webhook URL rotated. Update the WebHook in Jira with the new URL.');
  } catch (error) {
    logger.error('Failed to rotate the Jira webhook:', error);
    message.error(extractErrorDetail(error, 'Failed to rotate the Jira webhook.'));
  } finally {
    jiraWebhookActionLoading.value = false;
  }
}

async function handleDisableJiraWebhook(): Promise<void> {
  if (!editAgentUid.value) return;

  jiraWebhookActionLoading.value = true;
  try {
    jiraWebhookConfig.value = await disableAgentJiraWebhook(editAgentUid.value);
    message.success('Jira webhook disabled.');
    await loadIntakeAgents();
  } catch (error) {
    logger.error('Failed to disable the Jira webhook:', error);
    message.error(extractErrorDetail(error, 'Failed to disable the Jira webhook.'));
  } finally {
    jiraWebhookActionLoading.value = false;
  }
}

// ==================== Slack ====================

// `slack_enabled` is readable on IntakeAgentInDb but absent from IntakeAgentUpdate, and the one
// endpoint that changes it has no GET counterpart. So the agent row is where the flag is read
// from, the PUT's response is the only thing that can override it mid-modal, and the toggle
// takes effect immediately rather than being flushed by "Save Changes".
const slackOrgStatus = ref<SlackIntegrationStatus | null>(null);
const slackOrgStatusLoaded = ref(false);
const slackOrgStatusLoading = ref(false);
const slackActionLoading = ref(false);
const slackEnabledOverride = ref<boolean | null>(null);

const isSlackOrgConnected = computed(
  () => slackOrgStatus.value?.state === IntegrationState.CONNECTED
);

// Missing scopes only warn: the flag can still be set, it just will not do anything in Slack
// until the workspace is reconnected.
const slackNeedsReconnect = computed(
  () => isSlackOrgConnected.value && !hasAllSlackScopes(slackOrgStatus.value)
);

const isAgentSlackEnabled = computed(
  () => slackEnabledOverride.value ?? originalAgentData.value?.slack_enabled ?? false
);

// Same reasoning as the Jira org status: consent can be revoked server-side, so it is read when
// the tab is opened rather than cached alongside the agent list.
async function loadSlackOrgStatusIfNeeded(): Promise<void> {
  if (slackOrgStatusLoaded.value) return;

  slackOrgStatusLoading.value = true;
  try {
    slackOrgStatus.value = await fetchSlackIntegrationStatus();
    slackOrgStatusLoaded.value = true;
  } catch (error) {
    logger.error('Failed to load Slack status:', error);
    message.error(extractErrorDetail(error, 'Failed to load Slack status.'));
  } finally {
    slackOrgStatusLoading.value = false;
  }
}

async function handleToggleAgentSlack(enabled: boolean): Promise<void> {
  if (!editAgentUid.value) return;

  slackActionLoading.value = true;
  try {
    const config = await setAgentSlackEnabled(editAgentUid.value, enabled);
    slackEnabledOverride.value = config.enabled;
    message.success(config.enabled ? 'Agent available in Slack.' : 'Agent removed from Slack.');
    // The agent row is the only source for the flag, so without this refresh reopening the
    // modal would show the pre-toggle state.
    await loadIntakeAgents();
  } catch (error) {
    logger.error("Failed to update the agent's Slack setting:", error);
    message.error(extractErrorDetail(error, "Failed to update the agent's Slack setting."));
  } finally {
    slackActionLoading.value = false;
  }
}

/** The manual steps a Jira admin follows; Jira cannot be configured through our 3LO token. */
const JIRA_REGISTRATION_STEPS = [
  'Jira → Settings → System → WebHooks → Create a WebHook',
  'Paste the URL above verbatim — do not append anything to it.',
  'Tick exactly the events listed below.',
  'Add the JQL filter below, narrowed to the relevant projects.',
];

// Copy functionality
function copyToClipboard(text: string): void {
  navigator.clipboard
    .writeText(text)
    .then(() => message.success('Copied to clipboard!'))
    .catch(() => message.error('Failed to copy'));
}

// Copy API token functionality
function copyApiToken(): void {
  if (originalAgentData.value?.jwt_token) {
    copyToClipboard(originalAgentData.value.jwt_token);
  } else {
    message.warning('API token not available yet. Create the agent first to generate a token.');
  }
}

// Open API documentation
function openApiDocs(): void {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- falsy values must fall through here
  const apiBaseUrl = import.meta.env.VITE_API_URL || window.location.origin;
  const docsUrl = `${apiBaseUrl}/public-api/redoc`;
  window.open(docsUrl, '_blank');
}

// Load intake agents from API
async function loadIntakeAgents(): Promise<void> {
  isLoading.value = true;
  try {
    const query: SearchQuery = {
      text: '',
      limit: 50,
      offset: 0,
    };
    const response = await searchIntakeAgent({ body: query });
    if (response.error) {
      throw new Error(extractErrorDetail(response.error, 'Failed to load intake agents'));
    }

    intakeAgents.value = response.data.results;
  } catch (error) {
    message.error('Failed to load intake agents');
    logger.error('Failed to load intake agents', error);
  } finally {
    isLoading.value = false;
  }
}

// Load allowlist entries for the selected agent
async function loadAllowlistEntries(): Promise<void> {
  if (!selectedAgent.value) return;

  allowlistEntries.value = [];
  const loadingAllowlist = ref(true);

  try {
    const query: SearchQuery = {
      text: '',
      limit: 50,
      offset: 0,
    };
    const response = await searchAllowList({
      path: { intake_agent_uid: selectedAgent.value.uid },
      body: query,
    });
    if (response.error) {
      throw new Error(extractErrorDetail(response.error, 'Failed to load allowlist entries'));
    }

    allowlistEntries.value = response.data.results;
  } catch (error) {
    message.error('Failed to load allowlist entries');
    logger.error('Failed to load allowlist entries', error);
  } finally {
    loadingAllowlist.value = false;
  }
}

// Validate email format
// Load prompt collections when component mounts
async function loadPromptCollections(): Promise<void> {
  try {
    await promptCollectionsStore.fetchCollections();
  } catch (error) {
    message.error('Failed to load prompt collections');
    logger.error('Failed to load prompt collections', error);
  }
}

// Create a new allowlist entry
function createAllowlistEntryHandler(): void {
  const agent = selectedAgent.value;
  if (!agent) return;

  allowlistFormSubmitted.value = true;
  if (allowlistValueError.value) {
    message.error(allowlistValueError.value);
    return;
  }

  void (async (): Promise<void> => {
    try {
      // Prepare the allowlist entry based on type
      if (allowlistType.value === 'domain') {
        newAllowlistForm.value.domain = newAllowlistForm.value.email;
        newAllowlistForm.value.email = '';
        newAllowlistForm.value.is_domain = true;
        newAllowlistForm.value.is_user = false;
      } else if (allowlistType.value === 'user') {
        newAllowlistForm.value.is_user = true;
        newAllowlistForm.value.is_domain = false;
      } else {
        // Regular email
        newAllowlistForm.value.is_user = false;
        newAllowlistForm.value.is_domain = false;
      }

      await createAllowListEntry({
        path: { intake_agent_uid: agent.uid },
        body: newAllowlistForm.value,
      });

      message.success('Allowed sender created successfully');
      showNewAllowlistModal.value = false;

      // Reset form
      newAllowlistForm.value = {
        email: '',
        domain: '',
        is_user: false,
        is_domain: false,
      };
      allowlistType.value = 'email';

      await loadAllowlistEntries();

      // Since we're showing allowlist entries in the table, reload all agents to update their allowlist
      await loadIntakeAgents();
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message);
      } else {
        message.error('Failed to create allowlist entry');
      }
      logger.error('Failed to create allowlist entry', error);
    }
  })();
}

// Delete a allowlist entry
async function deleteAllowlistEntryHandler(entryUid: string): Promise<void> {
  try {
    await deleteAllowListEntry({
      path: { allow_list_entry_uid: entryUid },
    });

    message.success('Allowlist entry deleted successfully');
    await loadAllowlistEntries();
    // Refresh the table to show the updated allowlist entries
    await loadIntakeAgents();
  } catch (error) {
    message.error('Failed to delete allowlist entry');
    logger.error('Failed to delete allowlist entry', error);
  }
}

// Delete an intake agent
async function deleteIntakeAgentHandler(agentUid: string): Promise<void> {
  try {
    await deleteIntakeAgent({
      path: { intake_agent_uid: agentUid },
    });

    message.success('Intake agent deleted successfully');
    if (selectedAgent.value?.uid === agentUid) {
      selectedAgent.value = null;
      allowlistEntries.value = [];
    }
    await loadIntakeAgents();
  } catch (error) {
    message.error('Failed to delete intake agent');
    logger.error('Failed to delete intake agent', error);
  }
}

function selectAgent(agent: IntakeAgentInDb): void {
  selectedAgent.value = agent;
  void loadAllowlistEntries();
  void loadEquitySections();
}

/** The modal resets its own form on open and reports the agent back. */
function openNewAgentModal(): void {
  showNewAgentModal.value = true;
}

function handleAgentCreated(agent: IntakeAgentInDb): void {
  void loadIntakeAgents();
  selectAgent(agent);
}

// Function to add a new allowlist entry from the manager modal
function openAddAllowlistEntryModal(): void {
  if (!selectedAgent.value || selectedAgent.value.allow_all_senders) return;

  newAllowlistForm.value = {
    email: '',
    domain: '',
    is_user: false,
    is_domain: false,
  };
  allowlistType.value = 'email';
  allowlistFormSubmitted.value = false;

  showNewAllowlistModal.value = true;
}

async function openEditAgentModal(agent: IntakeAgentInDb): Promise<void> {
  originalAgentData.value = agent;

  // Determine capabilities based on existing data
  const capabilities: string[] = [];
  if (agent.prompt_collections.length > 0) {
    capabilities.push('review');
  }
  if (agent.equity_sections.length > 0) {
    capabilities.push('import_em');
  }
  if (agent.respond_with_summary) {
    capabilities.push('respond_with_summary');
  }
  if (agent.run_playbook) {
    capabilities.push('playbook');
  }

  // An agent's linked playbooks can be inactive, and the search only returns
  // active ones — so hand them to the field as seeds or the chips would read as
  // uids.
  const linkedPlaybooks = agent.playbooks;
  editPlaybookSeeds.value = linkedPlaybooks.map(playbook => ({
    label: playbook.name,
    value: playbook.uid,
  }));

  editAgentForm.value = {
    name: agent.name,
    description: agent.description ?? '',
    allow_all_senders: agent.allow_all_senders,
    allow_api_requests: agent.allow_api_requests,
    respond_with_summary: agent.respond_with_summary,
    prompt_collections: agent.prompt_collections.map(pc => pc.uid),
    playbooks: linkedPlaybooks.map(p => p.uid),
    clm_sections: 'none',
    capabilities,
    callback_webhook: agent.callback_webhook,
    filename_deny_list: agent.filename_deny_list,
    inbound_document_signing_status: agent.inbound_document_signing_status,
  };

  // Set filename denylist
  editFilenameDenylist.value = agent.filename_deny_list ? [...agent.filename_deny_list] : [];

  // Set allowlist entries
  editAllowlistEntries.value = agent.allow_list.map(entry => ({
    type: entry.is_domain
      ? ('domain' as const)
      : entry.is_user
        ? ('user' as const)
        : ('email' as const),
    value: entry.is_domain ? entry.domain : entry.email,
    uid: entry.uid,
  }));

  // Set webhook form data
  if (agent.callback_webhook) {
    webhookUrl.value = agent.callback_webhook.url;
    webhookMethod.value = agent.callback_webhook.method;
    webhookHeaders.value = agent.callback_webhook.headers ?? {};
    webhookTimeout.value =
      agent.callback_webhook.timeout_s === undefined
        ? '30'
        : agent.callback_webhook.timeout_s.toString();
    showWebhookForm.value = true;
  } else {
    webhookUrl.value = '';
    webhookMethod.value = 'POST';
    webhookHeaders.value = {};
    webhookTimeout.value = '30';
    showWebhookForm.value = false;
  }

  editSelectedSpecificSections.value = [];
  editSelectedDefaultSection.value = undefined;

  editAgentUid.value = agent.uid;
  showEditAgentModal.value = true;

  if (integrationsStore.activeIntegrations.gaia) {
    try {
      await loadClmSections();
    } catch (error) {
      logger.error('Failed to load CLM sections:', error);
      message.error('Failed to load CLM sections');
    }

    if (agent.equity_sections.length > 0) {
      const allAvailableSections = getAllAvailableSections();
      const agentSectionIds = new Set(
        agent.equity_sections.map(section => section.gaia_section_id)
      );

      if (
        allAvailableSections.length > 0 &&
        allAvailableSections.every(section => agentSectionIds.has(section.sectionId))
      ) {
        editAgentForm.value.clm_sections = 'all';
      } else {
        editSelectedSpecificSections.value = agent.equity_sections.map(
          section => section.gaia_section_id
        );
        editAgentForm.value.clm_sections = 'specific';
      }

      // Set default section if exists
      const id = agent.default_equity_section?.gaia_section_id;
      editSelectedDefaultSection.value = typeof id === 'string' ? id : undefined;
    }
  }
}

// Function to update an intake agent
function updateIntakeAgent(): void {
  // Set form as submitted so the fields show their errors inline
  editFormSubmitted.value = true;

  const capabilityErrors = [
    editNameError.value,
    editCapabilitiesError.value,
    editPromptCollectionsError.value,
  ].filter((error): error is string => Boolean(error));
  const advancedErrors = [webhookUrlError.value, webhookTimeoutError.value].filter(
    (error): error is string => Boolean(error)
  );

  if (capabilityErrors.length > 0 || advancedErrors.length > 0) {
    message.error(capabilityErrors[0] ?? advancedErrors[0]);
    editModalActiveTab.value = capabilityErrors.length > 0 ? 'capabilities' : 'advanced';
    return;
  }

  void (async (): Promise<void> => {
    isEditAgentSaving.value = true;
    try {
      const webhookUrlTrimmed = webhookUrl.value.trim();
      const descriptionTrimmed = editAgentForm.value.description?.trim() ?? '';
      const callbackWebhook =
        showWebhookForm.value && webhookUrlTrimmed && webhookUrlTrimmed.length > 0
          ? {
              method: webhookMethod.value,
              url: webhookUrlTrimmed,
              headers: Object.keys(webhookHeaders.value).length > 0 ? webhookHeaders.value : null,
              timeout_s: webhookTimeout.value ? Number(webhookTimeout.value) : undefined,
            }
          : null;
      const updateResponse = await updateIntakeAgentApi({
        path: { intake_agent_uid: editAgentUid.value },
        body: {
          name: editAgentForm.value.name,
          description: descriptionTrimmed.length > 0 ? descriptionTrimmed : null,
          allow_all_senders: editAgentForm.value.allow_all_senders,
          allow_api_requests: editAgentForm.value.allow_api_requests,
          respond_with_summary: editAgentForm.value.capabilities.includes('respond_with_summary'),
          run_playbook: editAgentForm.value.capabilities.includes('playbook'),
          callback_webhook: callbackWebhook,
          filename_deny_list:
            editFilenameDenylist.value.length > 0 ? editFilenameDenylist.value : null,
          inbound_document_signing_status: editAgentForm.value.inbound_document_signing_status,
        },
      });
      if (updateResponse.error) {
        throw new Error(extractErrorDetail(updateResponse.error, 'Failed to update intake agent'));
      }

      // Always update prompt collections - if review capability is removed, this will clear them
      if (editAgentForm.value.capabilities.includes('review')) {
        const promptCollectionsUpdate: IntakeAgentUpdatePromptCollections = {
          prompt_collection_uids: editAgentForm.value.prompt_collections,
        };

        const promptResponse = await updateIntakeAgentPromptCollections({
          path: { intake_agent_uid: editAgentUid.value },
          body: promptCollectionsUpdate,
        });
        if (promptResponse.error) {
          throw new Error(
            extractErrorDetail(
              promptResponse.error,
              "Failed to update the agent's prompt collections"
            )
          );
        }
      } else {
        // If review capability is removed, clear all prompt collections
        const promptCollectionsUpdate: IntakeAgentUpdatePromptCollections = {
          prompt_collection_uids: [],
        };

        const promptResponse = await updateIntakeAgentPromptCollections({
          path: { intake_agent_uid: editAgentUid.value },
          body: promptCollectionsUpdate,
        });
        if (promptResponse.error) {
          throw new Error(
            extractErrorDetail(
              promptResponse.error,
              "Failed to update the agent's prompt collections"
            )
          );
        }
      }

      // Always update playbooks - if playbook capability is removed, this will clear them
      const playbooksUpdate: IntakeAgentUpdatePlaybooks = {
        playbook_uids: editAgentForm.value.capabilities.includes('playbook')
          ? editAgentForm.value.playbooks
          : [],
      };

      const playbooksResponse = await updateIntakeAgentPlaybooks({
        path: { intake_agent_uid: editAgentUid.value },
        body: playbooksUpdate,
      });
      if (playbooksResponse.error) {
        throw new Error(
          extractErrorDetail(playbooksResponse.error, "Failed to update the agent's playbooks")
        );
      }

      // Delete existing sections first
      const existingSections = originalAgentData.value?.equity_sections ?? [];
      await Promise.all(
        existingSections.map(async section => {
          const deleteResponse = await deleteSection({
            path: { section_uid: section.uid },
          });
          if (deleteResponse.error) {
            logger.error('Failed to delete existing section:', deleteResponse.error);
          }
        })
      );

      // Create new equity sections efficiently if storage capability is enabled
      if (editAgentForm.value.capabilities.includes('import_em')) {
        await createEquitySections(
          editAgentUid.value,
          editAgentForm.value.clm_sections,
          editSelectedSpecificSections.value,
          typeof editSelectedDefaultSection.value === 'string'
            ? editSelectedDefaultSection.value
            : undefined
        );
      }

      message.success('Intake agent updated successfully');

      // Handle allowlist changes before clearing original data
      if (!editAgentForm.value.allow_all_senders) {
        // Get current allowlist from original data
        const currentAllowlist = originalAgentData.value?.allow_list ?? [];
        const formUids = new Set(
          editAllowlistEntries.value.map(entry => entry.uid).filter(Boolean)
        );

        // Delete removed entries
        for (const currentEntry of currentAllowlist) {
          if (!formUids.has(currentEntry.uid)) {
            try {
              const deleteResponse = await deleteAllowListEntry({
                path: { allow_list_entry_uid: currentEntry.uid },
              });
              if (deleteResponse.error) {
                throw new Error(
                  extractErrorDetail(deleteResponse.error, 'Failed to delete allowlist entry')
                );
              }
            } catch (error) {
              logger.error('Failed to delete allowlist entry:', error);
            }
          }
        }

        // Add new entries
        for (const formEntry of editAllowlistEntries.value) {
          if (!formEntry.uid && formEntry.value.trim()) {
            // Validate entry
            if (formEntry.type === 'domain' && !isValidDomain(formEntry.value)) {
              message.warning(`Invalid domain: ${formEntry.value}`);
              continue;
            } else if (formEntry.type !== 'domain' && !isValidEmail(formEntry.value)) {
              message.warning(`Invalid email: ${formEntry.value}`);
              continue;
            }

            try {
              const createResponse = await createAllowListEntry({
                path: { intake_agent_uid: editAgentUid.value },
                body: {
                  email: formEntry.type === 'domain' ? '' : formEntry.value,
                  domain: formEntry.type === 'domain' ? formEntry.value : '',
                  is_user: formEntry.type === 'user',
                  is_domain: formEntry.type === 'domain',
                },
              });
              if (createResponse.error) {
                throw new Error(
                  extractErrorDetail(createResponse.error, 'Failed to create allowlist entry')
                );
              }
            } catch (error) {
              logger.error('Failed to create allowlist entry:', error);
              message.warning(`Failed to add allowlist entry: ${formEntry.value}`);
            }
          }
        }
      }

      showEditAgentModal.value = false;

      // Clear the original agent data
      originalAgentData.value = null;

      // Refresh the data
      await loadIntakeAgents();
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message);
      } else {
        message.error('Failed to update intake agent');
      }
      logger.error('Failed to update intake agent', error);
    } finally {
      isEditAgentSaving.value = false;
    }
  })();
}

onMounted(async () => {
  await Promise.all([loadIntakeAgents(), loadPromptCollections()]);
});

// Define the intake agents table columns
const intakeAgentsColumns = computed(() => {
  const baseColumns = [
    {
      title: 'Name',
      key: 'name',
      // Allow for overflowing content to be truncated
      render(row: IntakeAgentInDb): VNode {
        return h(GaiaText, { truncate: true, size: 'row' }, { default: () => row.name });
      },
    },
    {
      title: 'Agent Email',
      key: 'email',
      width: '300px',
      render(row: IntakeAgentInDb): VNode {
        return h(
          NFlex,
          { align: 'center', size: 8, wrap: false },
          {
            default: () => [
              h(
                GaiaText,
                { size: 'row', tone: 'muted', truncate: true },
                { default: () => row.email }
              ),
              h(GaiaTooltip, null, {
                trigger: () =>
                  h(
                    GaiaIconButton,
                    {
                      dimension: 26,
                      quaternary: true,
                      onClick: (e: MouseEvent) => {
                        e.stopPropagation();
                        copyToClipboard(row.email);
                      },
                    },
                    { default: () => copyGlyph(13) }
                  ),
                default: () => 'Copy',
              }),
            ],
          }
        );
      },
    },
    {
      title: 'Capabilities',
      key: 'capabilities',
      render(row: IntakeAgentInDb): VNode {
        const capabilities: string[] = [];
        if (row.prompt_collections.length > 0) {
          capabilities.push('Review');
        }
        if (row.run_playbook) {
          capabilities.push('Playbook');
        }
        if (row.equity_sections.length > 0) {
          capabilities.push('Import to EM');
        }
        if (row.respond_with_summary) {
          capabilities.push('Respond with Summary');
        }
        if (row.jira_webhook_enabled) {
          capabilities.push('Jira WebHook');
        }

        if (capabilities.length === 0) {
          return h(GaiaText, { size: 'row', tone: 'muted' }, { default: () => 'None' });
        }

        return h(
          NFlex,
          { vertical: true, size: 4, wrap: false },
          {
            default: () =>
              capabilities.map(capability =>
                h(GaiaText, { size: 'row' }, { default: () => capability })
              ),
          }
        );
      },
    },
    {
      title: 'Allowlist',
      key: 'allowlist',
      render(row: IntakeAgentInDb): VNode {
        function addEntryButton(label: string): VNode {
          return h(
            GaiaButton,
            {
              size: 'small',
              onClick: (e: MouseEvent) => {
                e.stopPropagation();
                selectedAgent.value = row;
                openAddAllowlistEntryModal();
              },
            },
            { default: () => label }
          );
        }

        if (row.allow_all_senders) {
          return h(
            GaiaText,
            { size: 'row', tone: 'muted' },
            { default: () => 'All senders allowed' }
          );
        }

        if (row.allow_list.length === 0) {
          return h(
            NFlex,
            { align: 'center', justify: 'space-between', size: 8 },
            {
              default: () => [
                h(GaiaText, { size: 'row', tone: 'muted' }, { default: () => 'No entries' }),
                addEntryButton('Add'),
              ],
            }
          );
        }

        // Display allowlist entries with add/remove buttons
        return h(
          NFlex,
          { vertical: true, size: 6, wrap: false },
          {
            default: () => [
              ...row.allow_list.map((entry: AllowListInDb) => {
                const entryType = entry.is_domain ? 'Domain' : entry.is_user ? 'User' : 'Email';
                const entryValue = entry.is_domain ? entry.domain : entry.email;

                return h(
                  NFlex,
                  {
                    align: 'center',
                    justify: 'space-between',
                    size: 8,
                    wrap: false,
                    style: 'width: 100%',
                  },
                  {
                    default: () => [
                      h(
                        NFlex,
                        { align: 'center', size: 6, wrap: false },
                        {
                          default: () => [
                            h(
                              GaiaTag,
                              { variant: 'label', size: 'tiny' },
                              { default: () => entryType }
                            ),
                            h(
                              GaiaText,
                              { size: 'row', truncate: true },
                              { default: () => entryValue }
                            ),
                          ],
                        }
                      ),
                      h(
                        GaiaPopconfirm,
                        {
                          title: 'Delete this allowlist entry?',
                          width: 300,
                          onConfirm: () => {
                            void deleteAllowlistEntryHandler(entry.uid);
                          },
                        },
                        {
                          trigger: () =>
                            h(
                              GaiaIconButton,
                              {
                                dimension: 24,
                                quaternary: true,
                                type: 'error',
                                onClick: (e: MouseEvent) => {
                                  e.stopPropagation();
                                },
                              },
                              { default: () => trashGlyph(12) }
                            ),
                          default: () => 'The sender loses access to this agent immediately.',
                        }
                      ),
                    ],
                  }
                );
              }),
              addEntryButton('Add Entry'),
            ],
          }
        );
      },
    },
    {
      title: 'API Requests',
      key: 'api_requests',
      render(row: IntakeAgentInDb): VNode {
        if (!row.allow_api_requests) {
          return h(GaiaStatusChip, { tone: 'alert' }, { default: () => 'Blocked' });
        }

        const jwtToken = row.jwt_token;
        return h(
          NFlex,
          { align: 'center', size: 6, wrap: false },
          {
            default: () => [
              h(GaiaStatusChip, { tone: 'success' }, { default: () => 'Allowed' }),
              jwtToken
                ? h(GaiaTooltip, null, {
                    trigger: () =>
                      h(
                        GaiaIconButton,
                        {
                          dimension: 24,
                          quaternary: true,
                          onClick: (e: MouseEvent) => {
                            e.stopPropagation();
                            copyToClipboard(jwtToken);
                          },
                        },
                        { default: () => copyGlyph(12) }
                      ),
                    default: () => 'Copy API Token',
                  })
                : null,
              jwtToken
                ? h(
                    GaiaButton,
                    {
                      size: 'tiny',
                      onClick: (e: MouseEvent) => {
                        e.stopPropagation();
                        openApiDocs();
                      },
                    },
                    { default: () => 'Docs' }
                  )
                : null,
            ],
          }
        );
      },
    },
  ];

  if (integrationsStore.activeIntegrations.gaia) {
    baseColumns.push({
      title: 'Sections',
      key: 'sections',
      render(row: IntakeAgentInDb): VNode {
        if (row.equity_sections.length === 0) {
          return h(GaiaText, { size: 'row', tone: 'muted' }, { default: () => 'No sections' });
        }

        // Display all sections without trying to determine if it's "all sections"
        return h(
          NFlex,
          { vertical: true, size: 4, wrap: false },
          {
            default: () =>
              row.equity_sections.map((section: EquitySectionInDb) =>
                h(
                  NFlex,
                  { align: 'center', size: 6, wrap: false },
                  {
                    default: () => [
                      h(
                        GaiaTag,
                        { variant: 'label', size: 'tiny' },
                        { default: () => getGroupDisplayName(section) }
                      ),
                      h(
                        GaiaText,
                        { size: 'row', truncate: true },
                        { default: () => getSectionDisplayName(section) }
                      ),
                    ],
                  }
                )
              ),
          }
        );
      },
    });
  }

  // Add actions column
  baseColumns.push({
    title: 'Actions',
    key: 'actions',
    render(row: IntakeAgentInDb): VNode {
      return h(
        NFlex,
        { justify: 'center', align: 'center', size: 8, wrap: false },
        {
          default: () => [
            h(
              GaiaButton,
              {
                type: 'primary',
                size: 'small',
                onClick: (e: MouseEvent) => {
                  e.stopPropagation();
                  return openEditAgentModal(row);
                },
              },
              { default: () => 'Edit' }
            ),
            h(
              GaiaPopconfirm,
              {
                title: 'Delete this intake agent?',
                width: 320,
                onConfirm: () => {
                  void deleteIntakeAgentHandler(row.uid);
                },
              },
              {
                trigger: () =>
                  h(
                    GaiaButton,
                    {
                      type: 'error',
                      tertiary: true,
                      size: 'small',
                      onClick: (e: MouseEvent) => {
                        e.stopPropagation();
                      },
                    },
                    { default: () => 'Delete' }
                  ),
                default: () =>
                  'Its allowlist entries go with it, and forwarded mail stops arriving.',
              }
            ),
          ],
        }
      );
    },
  });

  return baseColumns;
});

// Define the allowlist entries table columns for the allowlist manager modal
const allowlistEntriesColumns = [
  {
    title: 'Type',
    key: 'type',
    render(row: AllowListInDb): VNode {
      return h(
        GaiaText,
        { size: 'row' },
        {
          default: () => (row.is_domain ? 'Domain' : row.is_user ? 'User' : 'Email'),
        }
      );
    },
  },
  {
    title: 'Value',
    key: 'value',
    render(row: AllowListInDb): VNode {
      return h(
        GaiaText,
        { size: 'row' },
        {
          default: () => (row.is_domain ? row.domain : row.email),
        }
      );
    },
  },
  {
    title: 'Actions',
    key: 'actions',
    render(row: AllowListInDb): VNode {
      return h(
        GaiaPopconfirm,
        {
          title: 'Delete this allowlist entry?',
          width: 300,
          onConfirm: () => {
            void deleteAllowlistEntryHandler(row.uid);
          },
        },
        {
          trigger: () =>
            h(
              GaiaButton,
              { type: 'error', tertiary: true, size: 'small' },
              { default: () => 'Delete' }
            ),
          default: () => 'The sender loses access to this agent immediately.',
        }
      );
    },
  },
];

function addHeader(): void {
  const key = newHeaderKey.value.trim();
  const value = newHeaderValue.value.trim();
  if (key && value) {
    webhookHeaders.value[key] = value;
    newHeaderKey.value = '';
    newHeaderValue.value = '';
  }
}

function removeHeader(key: string): void {
  delete webhookHeaders.value[key];
}

// Add this method for header editing
function updateHeaderValue(key: string, value: string): void {
  webhookHeaders.value = { ...webhookHeaders.value, [key]: value };
}

function headerValueUpdater(key: string): (val: string) => void {
  return val => {
    updateHeaderValue(key, val);
  };
}

// Add this method to clear all webhook fields
function clearWebhookSettings(): void {
  // Hide the webhook form
  showWebhookForm.value = false;

  // Set callback_webhook to null in the form
  editAgentForm.value = {
    ...editAgentForm.value,
    callback_webhook: null,
  };

  // Reset form fields to defaults for when form is shown again
  webhookUrl.value = '';
  webhookMethod.value = 'POST';
  webhookHeaders.value = {};
  webhookTimeout.value = '30';

  // Reset specific sections
  editSelectedSpecificSections.value = [];
  editSelectedDefaultSection.value = undefined;

  // Reset CLM sections to none
  editAgentForm.value.clm_sections = 'none';
}

// Add this method to show the webhook form again
function showWebhookSettings(): void {
  showWebhookForm.value = true;
}

async function loadEquitySections(): Promise<void> {
  if (!selectedAgent.value) return;

  try {
    const response = await searchEquitySections({
      path: { intake_agent_uid: selectedAgent.value.uid },
      body: {
        text: '',
        limit: 50,
        offset: 0,
      },
    });
    if (response.error) {
      throw new Error(extractErrorDetail(response.error, 'Failed to load equity sections'));
    }

    equitySections.value = response.data.results;
  } catch (error) {
    message.error('Failed to load equity sections');
    logger.error('Failed to load equity sections', error);
  }
}

function getSectionDisplayName(section: EquitySectionInDb): string {
  if (section.gaia_section_title) {
    return section.gaia_section_title;
  }
  return section.gaia_section_id;
}

function getGroupDisplayName(section: EquitySectionInDb): string {
  if (section.gaia_group_title) {
    return section.gaia_group_title;
  }
  return 'No group';
}

// Filename denylist management functions
function addFilenameDenylistItem(): void {
  editFilenameDenylist.value.push({ match_string: '', exact_match: false });
}

function removeFilenameDenylistItem(index: number): void {
  editFilenameDenylist.value.splice(index, 1);
}

// Allowlist form functions
function addEditAllowlistEntry(): void {
  editAllowlistEntries.value.push({ type: 'email', value: '' });
}

function removeEditAllowlistEntry(index: number): void {
  editAllowlistEntries.value.splice(index, 1);
}
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.table">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.organisation }}</n-breadcrumb-item>
      <n-breadcrumb-item>{{ navLabels.intakeAgents }}</n-breadcrumb-item>
    </template>

    <n-flex vertical :size="20" :wrap="false">
      <!-- Header Section -->
      <n-flex align="flex-start" :size="[20, 12]">
        <n-flex vertical :size="6" :style="{ minWidth: 0, flex: 1 }">
          <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
            {{ navLabels.intakeAgents }}
          </gaia-header>
          <gaia-text size="body" tone="tertiary" :style="{ lineHeight: 1.5 }">
            Intake agents allow you to forward documents to an agent via email. The agent will find
            the best prompt collection based on your selection and start a review in the review
            board for the submitted files.
          </gaia-text>
        </n-flex>
        <gaia-create-button subject="Agent" @click="openNewAgentModal()" />
      </n-flex>

      <!-- Intake Agents Table -->
      <gaia-spinner :show="isLoading">
        <gaia-table
          v-if="intakeAgents.length > 0"
          :columns="intakeAgentsColumns"
          :data="intakeAgents"
          :row-key="(row: IntakeAgentInDb) => row.uid"
          :single-line="false"
          :row-props="
            (row: IntakeAgentInDb) => ({
              onClick: () => selectAgent(row),
            })
          "
        />
        <gaia-empty-state v-else title="No intake agents yet">
          Create your first one to start forwarding documents by email.
          <template #actions>
            <gaia-button type="primary" size="small" @click="openNewAgentModal()">
              Create New Agent
            </gaia-button>
          </template>
        </gaia-empty-state>
      </gaia-spinner>
    </n-flex>

    <!-- Allowlist Manager Modal -->
    <gaia-modal
      v-model:show="showAllowlistManagerModal"
      :title="selectedAgent ? `Allowlist for ${selectedAgent.name}` : 'Allowlist Manager'"
      width="800px"
    >
      <n-flex v-if="selectedAgent" vertical :size="14" :wrap="false">
        <n-flex justify="end" :size="8">
          <gaia-button
            type="primary"
            size="small"
            :disabled="selectedAgent.allow_all_senders"
            @click="openAddAllowlistEntryModal"
          >
            Add Allowlist Entry
          </gaia-button>
        </n-flex>

        <gaia-empty-state
          v-if="selectedAgent.allow_all_senders"
          dashed
          title="Allowlist is disabled for this agent"
        >
          All senders are allowed.
        </gaia-empty-state>

        <template v-else>
          <gaia-table
            v-if="allowlistEntries.length > 0"
            :columns="allowlistEntriesColumns"
            :data="allowlistEntries"
            :row-key="(row: AllowListInDb) => row.uid"
            :single-line="false"
          />
          <gaia-empty-state v-else dashed title="No allowlist entries">
            Add some to control who can send emails to this agent.
          </gaia-empty-state>
        </template>
      </n-flex>

      <template #footer>
        <n-flex justify="end" :size="8" :wrap="false">
          <gaia-button size="small" @click="showAllowlistManagerModal = false">Close</gaia-button>
        </n-flex>
      </template>
    </gaia-modal>

    <intake-agent-create-modal v-model:show="showNewAgentModal" @created="handleAgentCreated" />

    <!-- Create New Allowlist Entry Modal -->
    <gaia-modal
      v-model:show="showNewAllowlistModal"
      :title="
        selectedAgent ? `Add Allowlist Entry for ${selectedAgent.name}` : 'Add Allowlist Entry'
      "
      width="600px"
    >
      <n-flex vertical :size="14" :wrap="false">
        <gaia-form-field label="Entry Type">
          <n-flex :size="8" :wrap="false">
            <gaia-button
              size="small"
              :type="allowlistType === 'user' ? 'primary' : 'default'"
              @click="allowlistType = 'user'"
            >
              User Email
            </gaia-button>
            <gaia-button
              size="small"
              :type="allowlistType === 'email' ? 'primary' : 'default'"
              @click="allowlistType = 'email'"
            >
              Email
            </gaia-button>
            <gaia-button
              size="small"
              :type="allowlistType === 'domain' ? 'primary' : 'default'"
              @click="allowlistType = 'domain'"
            >
              Domain
            </gaia-button>
          </n-flex>
        </gaia-form-field>

        <gaia-text size="control" tone="subtle">
          <template v-if="allowlistType === 'email'">
            Allow any sender with this email address
          </template>
          <template v-else-if="allowlistType === 'domain'">
            Allow any sender from this domain
          </template>
          <template v-else>
            Allow only registered and active users with this email address
          </template>
        </gaia-text>

        <gaia-form-field
          :label="allowlistType === 'domain' ? 'Domain' : 'Email'"
          required
          :error="(allowlistFormSubmitted && allowlistValueError) || undefined"
        >
          <gaia-input
            :value="newAllowlistForm.email"
            :placeholder="allowlistType === 'domain' ? 'example.com' : 'user@example.com'"
            :status="allowlistFormSubmitted && allowlistValueError ? 'error' : undefined"
            :style="{ width: '100%' }"
            @update:value="(value: string) => (newAllowlistForm.email = value)"
          />
        </gaia-form-field>
      </n-flex>

      <template #footer>
        <n-flex justify="end" :size="8" :wrap="false">
          <gaia-button size="small" @click="showNewAllowlistModal = false">Cancel</gaia-button>
          <gaia-button
            type="primary"
            size="small"
            :disabled="!selectedAgent"
            @click="createAllowlistEntryHandler"
          >
            Add
          </gaia-button>
        </n-flex>
      </template>
    </gaia-modal>

    <!-- Edit Agent Modal -->
    <gaia-modal v-model:show="showEditAgentModal" width="800px">
      <template #header>
        <n-flex align="center" :size="12" :wrap="false">
          <gaia-text size="dense" weight="semibold" tone="ink" :style="{ whiteSpace: 'nowrap' }">
            Agent Name:
          </gaia-text>
          <gaia-input
            v-model:value="editAgentForm.name"
            placeholder="Enter agent name"
            :style="{ flex: 1, maxWidth: '300px' }"
            :status="editFormSubmitted && editNameError ? 'error' : undefined"
          />
        </n-flex>
      </template>

      <n-flex vertical :size="16" :wrap="false">
        <gaia-tab-bar
          :items="AGENT_MODAL_TABS"
          :active="editModalActiveTab"
          @select="handleEditModalTabChange"
        />

        <!-- Capabilities Tab -->
        <n-flex v-show="editModalActiveTab === 'capabilities'" vertical :size="16" :wrap="false">
          <gaia-form-field label="Description" hint="Optional">
            <gaia-textarea
              v-model:value="editAgentForm.description"
              :rows="2"
              :autosize="false"
              placeholder="What this agent is for"
            />
          </gaia-form-field>

          <gaia-form-field
            label="Capabilities"
            required
            hint="Select the capabilities this intake agent will provide."
            :error="(editFormSubmitted && editCapabilitiesError) || undefined"
          >
            <gaia-select
              :value="editAgentForm.capabilities"
              multiple
              filterable
              placeholder="Select capabilities"
              :options="capabilitiesOptions"
              :status="editFormSubmitted && editCapabilitiesError ? 'error' : undefined"
              :style="{ width: '100%' }"
              @update:value="
                (value: string[]) => {
                  editAgentForm.capabilities = value;
                  // Clear prompt collections if review capability is removed
                  if (!value.includes('review')) {
                    editAgentForm.prompt_collections = [];
                  }
                  // Clear playbooks if playbook capability is removed
                  if (!value.includes('playbook')) {
                    editAgentForm.playbooks = [];
                  }
                }
              "
            />
          </gaia-form-field>

          <!-- Review Capability Section -->
          <n-flex
            v-if="editAgentForm.capabilities.includes('review')"
            vertical
            :size="10"
            :wrap="false"
          >
            <gaia-section-header variant="rule">Review Capability Settings</gaia-section-header>
            <gaia-form-field
              label="Prompt Collections"
              required
              hint="The prompt collections that can run on files submitted to this agent. The system will automatically choose the best matching collection."
              :error="(editFormSubmitted && editPromptCollectionsError) || undefined"
            >
              <gaia-select
                v-model:value="editAgentForm.prompt_collections"
                multiple
                filterable
                placeholder="Select prompt collections"
                :options="promptCollectionOptions"
                :status="editFormSubmitted && editPromptCollectionsError ? 'error' : undefined"
                :style="{ width: '100%' }"
              />
            </gaia-form-field>
          </n-flex>

          <!-- Playbook Capability Section -->
          <n-flex
            v-if="editAgentForm.capabilities.includes('playbook')"
            vertical
            :size="10"
            :wrap="false"
          >
            <gaia-section-header variant="rule">Playbook Capability Settings</gaia-section-header>
            <gaia-form-field
              label="Playbooks"
              hint="The playbooks this agent can run on submitted files."
            >
              <gaia-remote-select
                v-model:value="editAgentForm.playbooks"
                multiple
                clearable
                placeholder="Search playbooks"
                :debounce="400"
                :fetch="fetchPlaybooks"
                :seed-options="editPlaybookSeeds"
                :style="{ width: '100%' }"
                @error="onPlaybookSearchError"
              />
            </gaia-form-field>
          </n-flex>

          <!-- Storage Capability Section -->
          <n-flex
            v-if="
              editAgentForm.capabilities.includes('import_em') &&
              integrationsStore.activeIntegrations.gaia
            "
            vertical
            :size="10"
            :wrap="false"
          >
            <gaia-section-header variant="rule">
              Storage Capability Settings (CLM)
            </gaia-section-header>

            <gaia-form-field label="Are the documents signed?">
              <gaia-select
                v-model:value="editAgentForm.inbound_document_signing_status"
                :options="SIGNING_STATUS_OPTIONS"
                :style="{ width: '100%' }"
              />
            </gaia-form-field>

            <gaia-form-field label="Where to store">
              <gaia-select
                v-model:value="editAgentForm.clm_sections"
                filterable
                placeholder="Select CLM section option"
                :options="CLM_SECTION_OPTIONS"
                :disabled="isLoadingClmSections"
                :loading="isLoadingClmSections"
                :style="{ width: '100%' }"
              />
            </gaia-form-field>

            <!-- Conditional dropdown for specific sections -->
            <gaia-form-field
              v-if="editAgentForm.clm_sections === 'specific'"
              label="Sorting Sections"
              hint="The possible CLM sections where the file should be stored. The system will automatically choose the best matching section."
            >
              <gaia-select
                v-model:value="editSelectedSpecificSections"
                :options="gaiaSectionOptions"
                placeholder="Select specific sections..."
                multiple
                filterable
                :loading="isLoadingClmSections"
                :disabled="isLoadingClmSections"
                :style="{ width: '100%' }"
              />
            </gaia-form-field>

            <!-- Default section selection -->
            <gaia-form-field
              v-if="
                editAgentForm.clm_sections === 'specific' || editAgentForm.clm_sections === 'all'
              "
              label="Default Section"
              hint="This section will be chosen if the document can't be processed technically or the AI can't decide for a matching section."
            >
              <gaia-select
                v-model:value="editSelectedDefaultSection"
                :options="gaiaSectionOptions"
                placeholder="Select default section (optional)..."
                filterable
                clearable
                :loading="isLoadingClmSections"
                :disabled="isLoadingClmSections"
                :style="{ width: '100%' }"
              />
            </gaia-form-field>
          </n-flex>
        </n-flex>

        <!-- Permissions Tab -->
        <n-flex v-show="editModalActiveTab === 'permissions'" vertical :size="12" :wrap="false">
          <gaia-toggle-row
            card
            label="Allow All Senders"
            :hint="
              editAgentForm.allow_all_senders
                ? 'All senders allowed'
                : 'Only allowlist entries allowed'
            "
            :checked="Boolean(editAgentForm.allow_all_senders)"
            @update:checked="editAgentForm.allow_all_senders = $event"
          />

          <gaia-toggle-row
            card
            label="API Access"
            :hint="
              editAgentForm.allow_api_requests ? 'API requests allowed' : 'API requests blocked'
            "
            :checked="Boolean(editAgentForm.allow_api_requests)"
            @update:checked="editAgentForm.allow_api_requests = $event"
          />

          <n-flex
            v-if="editAgentForm.allow_api_requests && originalAgentData?.jwt_token"
            vertical
            :size="6"
            :wrap="false"
          >
            <n-flex :size="8">
              <gaia-button size="small" @click="copyApiToken">Copy API Token</gaia-button>
              <gaia-button size="small" @click="openApiDocs">View API Docs</gaia-button>
            </n-flex>
            <gaia-text size="micro" tone="muted">
              Copy the JWT token for API authentication
            </gaia-text>
          </n-flex>

          <!-- Allowlist Management (only show if allow_all_senders is false) -->
          <n-flex v-if="!editAgentForm.allow_all_senders" vertical :size="10" :wrap="false">
            <gaia-section-header variant="rule">Allowlist Entries</gaia-section-header>
            <gaia-text size="control" tone="subtle">
              Only senders in the allowlist will be able to send emails to this agent.
            </gaia-text>

            <n-flex
              v-for="(entry, index) in editAllowlistEntries"
              :key="index"
              align="center"
              :size="8"
              :wrap="false"
            >
              <gaia-select
                v-model:value="entry.type"
                :options="ALLOWLIST_ENTRY_TYPE_OPTIONS"
                :style="{ width: '110px', flex: 'none' }"
              />
              <gaia-input
                v-model:value="entry.value"
                :placeholder="entry.type === 'domain' ? 'example.com' : 'user@example.com'"
                :style="{ flex: 1 }"
              />
              <gaia-delete-button intent="remove" @click="removeEditAllowlistEntry(index)" />
            </n-flex>

            <gaia-add-button
              subject="Allowlist Entry"
              variant="dashed"
              @click="addEditAllowlistEntry"
            />
          </n-flex>
        </n-flex>

        <!-- Advanced Settings Tab -->
        <n-flex v-show="editModalActiveTab === 'advanced'" vertical :size="16" :wrap="false">
          <!-- Webhook Settings Section -->
          <n-flex vertical :size="10" :wrap="false">
            <gaia-section-header variant="rule">Webhook Notifications</gaia-section-header>
            <gaia-text size="control" tone="subtle">
              Get notified when documents are processed by this agent.
            </gaia-text>

            <template v-if="showWebhookForm">
              <gaia-form-field
                label="Webhook URL"
                required
                hint="Receives a notification when documents are processed by this agent."
                :error="(editFormSubmitted && webhookUrlError) || undefined"
              >
                <gaia-input
                  v-model:value="webhookUrl"
                  placeholder="Enter callback webhook URL"
                  clearable
                  :status="editFormSubmitted && webhookUrlError ? 'error' : undefined"
                  :style="{ width: '100%' }"
                />
              </gaia-form-field>

              <n-flex :size="12" align="flex-start">
                <gaia-form-field label="HTTP Method" :style="{ width: '200px' }">
                  <gaia-select
                    v-model:value="webhookMethod"
                    :options="WEBHOOK_METHOD_OPTIONS"
                    placeholder="Select HTTP method"
                    :style="{ width: '100%' }"
                  />
                </gaia-form-field>
                <gaia-form-field
                  label="Timeout (seconds)"
                  :style="{ width: '200px' }"
                  :error="(editFormSubmitted && webhookTimeoutError) || undefined"
                >
                  <gaia-input
                    v-model:value="webhookTimeout"
                    placeholder="30"
                    :status="editFormSubmitted && webhookTimeoutError ? 'error' : undefined"
                    :style="{ width: '100%' }"
                  />
                </gaia-form-field>
              </n-flex>

              <gaia-form-field label="Headers" hint="Optional">
                <n-flex vertical :size="8" :wrap="false" :style="{ width: '100%' }">
                  <n-flex
                    v-for="(value, key, idx) in webhookHeaders"
                    :key="key + idx"
                    align="center"
                    :size="8"
                    :wrap="false"
                  >
                    <gaia-input
                      :value="key"
                      disabled
                      placeholder="Header Key"
                      :style="{ width: '160px' }"
                    />
                    <gaia-input
                      :value="value"
                      placeholder="Header Value"
                      :style="{ flex: 1 }"
                      @update:value="headerValueUpdater(key)"
                    />
                    <gaia-delete-button intent="remove" @click="removeHeader(key)" />
                  </n-flex>

                  <n-flex align="center" :size="8" :wrap="false">
                    <gaia-input
                      v-model:value="newHeaderKey"
                      placeholder="Header Key"
                      :style="{ width: '160px' }"
                    />
                    <gaia-input
                      v-model:value="newHeaderValue"
                      placeholder="Header Value"
                      :style="{ flex: 1 }"
                    />
                    <gaia-button size="small" @click="addHeader">Add Header</gaia-button>
                  </n-flex>
                </n-flex>
              </gaia-form-field>

              <n-flex justify="end" :size="8">
                <gaia-button size="small" type="error" tertiary @click="clearWebhookSettings">
                  Remove Webhook
                </gaia-button>
              </n-flex>
            </template>

            <gaia-add-button
              v-else
              subject="Webhook Settings"
              variant="dashed"
              @click="showWebhookSettings"
            />
          </n-flex>

          <!-- Filename Exclude List Section -->
          <n-flex vertical :size="10" :wrap="false">
            <gaia-section-header variant="rule">Filename Exclude List</gaia-section-header>
            <gaia-text size="control" tone="subtle">
              Block files with specific names or extensions from being processed by this agent.
            </gaia-text>

            <n-flex
              v-for="(item, index) in editFilenameDenylist"
              :key="index"
              align="center"
              :size="8"
              :wrap="false"
            >
              <gaia-input
                v-model:value="item.match_string"
                placeholder="e.g., .pdf or filename.txt"
                :style="{ flex: 1 }"
              />
              <gaia-switch v-model:value="item.exact_match" :style="{ flex: 'none' }" />
              <gaia-text size="micro" tone="muted" :style="{ width: '96px', flex: 'none' }">
                {{ item.exact_match ? 'Exact match' : 'Contains' }}
              </gaia-text>
              <gaia-delete-button intent="remove" @click="removeFilenameDenylistItem(index)" />
            </n-flex>

            <gaia-add-button
              subject="Exclude List Entry"
              variant="dashed"
              @click="addFilenameDenylistItem"
            />
          </n-flex>
        </n-flex>

        <!-- Jira Tab -->
        <n-flex v-show="editModalActiveTab === 'jira'" vertical :size="12" :wrap="false">
          <gaia-spinner :show="jiraWebhookLoading">
            <n-flex vertical :size="12" :wrap="false">
              <gaia-section-header variant="rule">
                Jira WebHook
                <template #extra>
                  <gaia-status-chip :tone="isJiraWebhookEnabled ? 'success' : 'neutral'">
                    {{ isJiraWebhookEnabled ? 'Enabled' : 'Disabled' }}
                  </gaia-status-chip>
                </template>
              </gaia-section-header>

              <gaia-text size="control" tone="subtle" :style="{ lineHeight: 1.5 }">
                Let this agent pick up files attached to Jira issues. The WebHook has to be
                registered in Jira by hand — we cannot create it for you. Enabling, rotating and
                disabling take effect immediately: they are not part of "Save Changes".
              </gaia-text>

              <gaia-banner v-if="jiraWebhookLoaded && !isJiraOrgConnected" type="warning" plain>
                Jira is not connected for this organisation, so incoming events are acknowledged and
                then dropped. Connect Jira under
                <router-link to="/settings/service-account-token">Integrations</router-link>.
              </gaia-banner>

              <template v-if="jiraWebhookConfig?.enabled && jiraWebhookConfig.webhook_url">
                <gaia-form-field
                  label="WebHook URL"
                  hint="This URL is a secret — treat it like an API key."
                >
                  <n-flex align="center" :size="8" :wrap="false" :style="{ width: '100%' }">
                    <gaia-password-input
                      :value="jiraWebhookConfig.webhook_url"
                      readonly
                      :style="{ flex: 1 }"
                    />
                    <gaia-tooltip>
                      <template #trigger>
                        <gaia-icon-button
                          :dimension="34"
                          title="Copy WebHook URL"
                          @click="copyToClipboard(jiraWebhookConfig?.webhook_url ?? '')"
                        >
                          <svg
                            width="15"
                            height="15"
                            viewBox="0 0 16 16"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <rect x="5.5" y="5.5" width="8" height="8" rx="1.6" />
                            <path
                              d="M10.5 3.2A1.7 1.7 0 0 0 8.8 2H3.7A1.7 1.7 0 0 0 2 3.7v5.1c0 .8.5 1.4 1.2 1.6"
                            />
                          </svg>
                        </gaia-icon-button>
                      </template>
                      Copy WebHook URL
                    </gaia-tooltip>
                  </n-flex>
                </gaia-form-field>

                <n-flex vertical :size="10" :wrap="false">
                  <gaia-section-header variant="rule">Register it in Jira</gaia-section-header>

                  <n-flex
                    v-for="(step, index) in JIRA_REGISTRATION_STEPS"
                    :key="index"
                    :size="8"
                    :wrap="false"
                    align="flex-start"
                  >
                    <gaia-text
                      variant="label"
                      size="micro"
                      tone="faint"
                      :style="{ flex: 'none', paddingTop: '2px' }"
                    >
                      {{ index + 1 }}
                    </gaia-text>
                    <gaia-text size="control" tone="text" :style="{ lineHeight: 1.5 }">
                      {{ step }}
                    </gaia-text>
                  </n-flex>

                  <n-flex align="center" :size="6">
                    <gaia-text variant="label" size="micro" tone="faint">Events</gaia-text>
                    <gaia-tag
                      v-for="event in jiraWebhookConfig.events"
                      :key="event"
                      size="tiny"
                      variant="muted"
                    >
                      {{ event }}
                    </gaia-tag>
                  </n-flex>

                  <n-flex align="flex-end" :size="8" :wrap="false">
                    <gaia-code-box label="JQL filter" :style="{ flex: 1 }">
                      {{ jiraWebhookConfig.jql_hint }}
                    </gaia-code-box>
                    <gaia-button
                      size="small"
                      @click="copyToClipboard(jiraWebhookConfig?.jql_hint ?? '')"
                    >
                      Copy
                    </gaia-button>
                  </n-flex>
                  <gaia-text size="micro" tone="muted" :style="{ lineHeight: 1.5 }">
                    Without a JQL filter the agent receives every issue event on the whole site.
                  </gaia-text>
                </n-flex>

                <n-flex :size="8">
                  <gaia-popconfirm
                    title="Rotate the webhook URL?"
                    confirm-label="Rotate URL"
                    :width="320"
                    @confirm="handleRotateJiraWebhook"
                  >
                    <template #trigger>
                      <gaia-button size="small" :loading="jiraWebhookActionLoading">
                        Rotate URL
                      </gaia-button>
                    </template>
                    The current URL stops working immediately. Paste the new URL into Jira before
                    events will flow again.
                  </gaia-popconfirm>

                  <gaia-popconfirm
                    title="Disable the Jira webhook?"
                    confirm-label="Disable"
                    :width="320"
                    @confirm="handleDisableJiraWebhook"
                  >
                    <template #trigger>
                      <gaia-button
                        size="small"
                        type="error"
                        tertiary
                        :loading="jiraWebhookActionLoading"
                      >
                        Disable
                      </gaia-button>
                    </template>
                    The URL registered in Jira stops resolving — every event it sends will 404 until
                    the webhook is enabled again.
                  </gaia-popconfirm>
                </n-flex>
              </template>

              <n-flex v-else-if="jiraWebhookLoaded" vertical :size="10" :wrap="false">
                <gaia-text size="control" tone="subtle" :style="{ lineHeight: 1.5 }">
                  No WebHook URL exists for this agent yet. Enabling one mints the URL you then
                  register in Jira.
                </gaia-text>
                <n-flex>
                  <gaia-button
                    type="primary"
                    size="small"
                    :loading="jiraWebhookActionLoading"
                    @click="handleEnableJiraWebhook"
                  >
                    Enable Jira WebHook
                  </gaia-button>
                </n-flex>
              </n-flex>

              <gaia-empty-state
                v-else-if="!jiraWebhookLoading"
                dashed
                status="alert"
                title="Couldn't load the Jira WebHook configuration"
              >
                <template #actions>
                  <gaia-button size="small" @click="loadJiraWebhookIfNeeded">Retry</gaia-button>
                </template>
              </gaia-empty-state>
            </n-flex>
          </gaia-spinner>
        </n-flex>

        <!-- Slack Tab -->
        <n-flex v-show="editModalActiveTab === 'slack'" vertical :size="12" :wrap="false">
          <gaia-spinner :show="slackOrgStatusLoading">
            <n-flex vertical :size="12" :wrap="false">
              <gaia-section-header variant="rule">
                Slack
                <template #extra>
                  <gaia-status-chip :tone="isAgentSlackEnabled ? 'success' : 'neutral'">
                    {{ isAgentSlackEnabled ? 'Enabled' : 'Disabled' }}
                  </gaia-status-chip>
                </template>
              </gaia-section-header>

              <gaia-text size="control" tone="subtle" :style="{ lineHeight: 1.5 }">
                Let workspace users pick this agent in Slack's /gaia-submit. The toggle takes effect
                immediately: it is not part of "Save Changes".
              </gaia-text>

              <gaia-banner v-if="slackOrgStatusLoaded && !isSlackOrgConnected" type="warning" plain>
                Slack is not connected for this organisation, so this agent will not appear in
                /gaia-submit. Connect Slack under
                <router-link to="/settings/service-account-token">Integrations</router-link>.
              </gaia-banner>

              <gaia-banner v-else-if="slackNeedsReconnect" type="warning" plain>
                This Slack workspace is missing permissions Gaia now needs, so the agent will not
                show up in /gaia-submit even once enabled. Reconnect Slack under
                <router-link to="/settings/service-account-token">Integrations</router-link>.
              </gaia-banner>

              <gaia-toggle-row
                card
                label="Available in Slack"
                hint="Shown in the /gaia-submit agent picker."
                :checked="isAgentSlackEnabled"
                :disabled="slackActionLoading"
                @update:checked="handleToggleAgentSlack"
              />
            </n-flex>
          </gaia-spinner>
        </n-flex>

        <!-- Warning note below tabs - always visible -->
        <gaia-banner v-if="editAgentForm.allow_all_senders" type="info" title="Note">
          Everyone can send emails to this agent. Toggle off "Allow All Senders" in the Permissions
          tab to configure specific allowlist entries.
        </gaia-banner>
      </n-flex>

      <template #footer>
        <n-flex justify="end" :size="8" :wrap="false">
          <gaia-button size="small" @click="showEditAgentModal = false">Cancel</gaia-button>
          <gaia-button
            type="primary"
            size="small"
            :loading="isEditAgentSaving"
            :disabled="isEditAgentSaving"
            @click="updateIntakeAgent"
          >
            Save Changes
          </gaia-button>
        </n-flex>
      </template>
    </gaia-modal>
  </Layout>
</template>
